import * as _ from 'lodash-es'
import { defineStore } from 'pinia'

import api from '@/api/api'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useSettingStore } from '@/stores/setting'
import { useUserStore } from '@/stores/user'
import { byId, deleteById, upsertById } from '@/utils/utils'

import { PerformanceReportRead, ResidencyId, ResidencyRead, TypedId } from '@generated/types'

export type NewResidencyId = TypedId<'new-residency-'> // new-residency-<unique-id>
export type StoreResidencyId = ResidencyId | NewResidencyId
export type StoreResidencyRead = {
  id: StoreResidencyId
  show: null | Pick<ResidencyRead, 'show'>
  show_id: null | Pick<ResidencyRead, 'show_id'>
} & Omit<ResidencyRead, 'show' | 'show_id' | 'id'>

interface ResidencyState {
  residencies: StoreResidencyRead[]
  selectedResidency: StoreResidencyRead | null
  selectedResidencyPerformanceReports: PerformanceReportRead[]
}

export interface SelectedResidencyDashboard {
  netRevenue: number
  totalOfGuests: number
  discountAmount: number
  refundAmount: number
  addonAmount: number
}

export const useResidencyStore = defineStore({
  id: 'residency',
  state: (): ResidencyState => ({
    residencies: [],
    selectedResidency: null,
    selectedResidencyPerformanceReports: [],
  }),
  getters: {
    selectedResidencyDashboard(): SelectedResidencyDashboard {
      return {
        netRevenue: _.sumBy(this.selectedResidencyPerformanceReports, r => +r.net_face),
        totalOfGuests: _.sumBy(this.selectedResidencyPerformanceReports, r => +r.booked_guests),
        discountAmount: _.sumBy(this.selectedResidencyPerformanceReports, r => +r.discounted_sum),
        refundAmount: _.sumBy(this.selectedResidencyPerformanceReports, r => +r.refunded_sum),
        addonAmount: 0, // TODO: implement this
      }
    },
  },
  actions: {
    isNewResidency(id: StoreResidencyId): id is NewResidencyId {
      return typeof id === 'string'
    },
    storeResidency(rawResidency: ResidencyRead): ResidencyRead {
      upsertById(rawResidency, this.residencies)
      return rawResidency
    },
    async getResidencies() {
      const response = await api.getResidencies()
      response.data.map(this.storeResidency)
    },
    async getResidency(residencyId: ResidencyId | string) {
      const response = await api.getResidency(residencyId)
      return response.data
    },
    residencyById(id: StoreResidencyId): StoreResidencyRead | undefined {
      return this.residencies.find(byId(id))
    },
    setSelectedResidency(residency: StoreResidencyRead | null) {
      this.selectedResidency = _.cloneDeep(residency)
      if (!residency) this.selectedResidencyPerformanceReports = []
    },
    async getSelectedResidencyReports() {
      if (!this.selectedResidency) {
        this.selectedResidencyPerformanceReports = []
        return
      }

      const userStore = useUserStore()
      const settingStore = useSettingStore()
      const performanceStore = usePerformanceStore()
      const sortedPerformances = _.sortBy(this.selectedResidency.performances, p => p.datetime)
      const startDate = _.head(sortedPerformances)?.datetime
      const endDate = _.last(sortedPerformances)?.datetime

      // in case it is a new residency
      if (!startDate || !endDate) return
      if (userStore.isOwner) {
        const performanceReports = await performanceStore.getPerformanceReports({
          start_date: settingStore.dateIsoStr(startDate),
          end_date: settingStore.dateIsoStr(endDate),
        })
        // Only show performances that are part of the residency
        this.selectedResidencyPerformanceReports = performanceReports.filter(p =>
          this.selectedResidency!.performance_ids.includes(p.id),
        )
      } else {
        this.selectedResidencyPerformanceReports =
          this.selectedResidency.performances.map<PerformanceReportRead>(p => ({
            id: p.id,
            created: p.datetime,
            datetime: p.datetime,
            publish_datetime: p.publish_datetime,
            show: p.show,
            show_id: p.show_id,
            sold: p.sold,
            refunded_sum: 0,
            tax_sum: 0,
            booked_guests: 0,
            table_count: 0,
            booked_tables: 0,
            service_fee_sum: 0,
            amount_sum: 0,
            discounted_sum: 0,
            net_face: 0,
          }))
      }
    },
    async deleteResidency(id: StoreResidencyId) {
      if (!this.isNewResidency(id)) await api.deleteResidency(id)
      deleteById(id, this.residencies)
      this.setSelectedResidency(null)
    },
    createResidency(): StoreResidencyRead {
      return upsertById(
        {
          id: _.uniqueId('new-residency-'),
          name: 'New Residency',
          show: null,
          show_id: null,
          performances: [],
          performance_ids: [],
          start_date: '',
          end_date: '',
          slug: '',
        },
        this.residencies,
      )
    },
    removeNewResidencies() {
      this.residencies = this.residencies.filter(r => !this.isNewResidency(r.id))
    },
  },
})
